import React, { useState, useEffect, useRef } from 'react';
import { Card, Btn as Button, Input, Select, SearchableSelect, DatePicker, Modal } from '../components/UI';
import { listMaterials, createBOQ, updateBOQ, listDmrSubsections, listDmrCategories, listProjectDivisions } from '../api';
import { costLine, ratesFor } from '../utils/boqCosting';

const GLOBAL_PARAMS = {
  installAccessoriesRate: 0.10,
  transportSiteRate: 0.05,
  labourRateHour: 5000,
  equipmentRateHour: 625,
  prelimsRate: 0, // currently switched off in quote
  marginMultiplier: 1.35,
  marginRate: 0.35,
};

export default function BOQFormPage({ project, onBack, toast, existingBoqData, hideBackButton = false }) {
  const projectId = project?.project_id || project?.id;
  const tableContainerRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [header, setHeader] = useState({
    name: '',
    description: '',
    entry_date: new Date().toISOString().split('T')[0],
    attention: '',
    vat_rate: 7.5,
    // pricing rates (the Excel BOQ template's; see src/utils/boqCosting.js) — percentages as typed, ₦ per man-hour
    landed_uplift_pct: 8,
    install_access_rate_pct: 5,
    transport_rate_pct: 3,
    prelims_rate_pct: 15,
    equip_rate: 12,          // % of labour
    labour_rate: 4500,       // labour gang rate, ₦ per man-hour
    margin_mat_rate_pct: 10,
    margin_lab_rate_pct: 20,
    ex_works_pds_rate: 650,
    ex_works_euro_rate: 600,
    ex_works_usd_rate: 550,
    fob_usd_rate: 15,
    sea_freight_rate: 15,
    insurance_comsn_others_rate: 3,
    cf_naira_rate: 550,
    duty_surcharges_rate: 15,
    clearing_charges_rate: 10,
    agency_other_charges_rate: 5,
    agency_other_charges_rate: 5,
    boq_revision_id: '',
    building_ids: [],        // the BOQ's building/zone tabs, in order
    status: 'active',
  });

  const emptyRow = {
    boq_code: '', description: '', material_id: '', unit_of_measure: 'PCS', budgeted_quantity: '', consultant_quantity: '', basic_rate_material: '', factor: 1, unit_hour: '',
    price_source_amount: 0, ex_works_pds_rate: 650, ex_works_pds_amount: 0, ex_works_euro_rate: 600, ex_works_euro_amount: 0,
    ex_works_usd_rate: 550, ex_works_usd_amount: 0, fob_usd_rate: 0.15, fob_usd_amount: 0, sea_freight_rate: 0.15, sea_freight_amount: 0,
    insurance_comsn_others_rate: 0.03, insurance_comsn_others_amount: 0, cf_usd_amount: 0, cf_naira_rate: 550, cf_naira_amount: 0,
    duty_surcharges_rate: 0.15, duty_surcharges_amount: 0, clearing_charges_rate: 0.10, clearing_charges_amount: 0,
    agency_other_charges_rate: 0.05, agency_other_charges_amount: 0,
    landed_cost_materials: 0, install_rate_labour: 0, unit_rate: 0, amount: 0, installation_accessories_consumables_rate: 0,
    installation_accessories_consumables: 0, transport_to_site_rate: 0, transport_to_site: 0, unit_rate_material: 0, total_material_delivered_site: 0,
    unit_labour_rate: 0, unit_labour: 0, total_labour: 0, equipment_and_tools_rate: 0, equipment_and_tools: 0, prelim_site_mgmt_supevission_rate: 0,
    prelim_site_mgmt_supevission: 0, total_unit_rate_mat_lab: 0, total_amount_mat_lab: 0, margin_markup_material_rate: 0, margin_markup_material_amount: 0, margin_markup_labour_rate: 0, margin_markup_labour_amount: 0,
    has_components: false, components: []
  };

  const emptyComponent = { ...emptyRow, has_components: false, components: [] };

  const [sections, setSections] = useState([{ project_division_id: '', dmr_subsection_id: '', dmr_category_id: '', items: [{ ...emptyRow }] }]);
  const [materialsList, setMaterialsList] = useState([]);
  const [subsections, setSubsections] = useState([]);
  const [categories, setCategories] = useState([]);
  const [projectDivisions, setProjectDivisions] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [matRes, subRes, catRes, divRes] = await Promise.all([
          listMaterials(),
          listDmrSubsections(projectId),
          listDmrCategories(),
          listProjectDivisions(projectId)
        ]);
        setMaterialsList(Array.isArray(matRes) ? matRes : (matRes.data || []));
        setSubsections(Array.isArray(subRes) ? subRes : (subRes.data || []));
        setCategories(Array.isArray(catRes) ? catRes : (catRes.data || []));
        setProjectDivisions(Array.isArray(divRes) ? divRes : (divRes.data || []));

        if (existingBoqData) {
          setHeader(prev => ({
            ...prev,
            name: existingBoqData.name || '',
            description: existingBoqData.description || '',
            entry_date: existingBoqData.entry_date || prev.entry_date,
            attention: existingBoqData.attention || '',
            vat_rate: (existingBoqData.vat_rate * 100) || 7.5, // backend stores as 0.075 usually
            boq_revision_id: existingBoqData.boq_revision_id || '',
            building_ids: (existingBoqData.buildings || []).map(b => b.project_division_id),
            status: existingBoqData.status || 'active',
            ...ratesToHeader(ratesFor({}, existingBoqData.boq_items || [])),
          }));

          const items = existingBoqData.boq_items || [];
          if (items.length > 0) {
            const groups = {};
            items.forEach(item => {
              const subId = item.dmr_subsection_id || '';
              const catId = item.dmr_category_id || '';
              const divId = item.project_division_id || '';
              const key = `${divId}_${subId}_${catId}`;
              if (!groups[key]) groups[key] = { divId, subId, catId, items: [] };
              groups[key].items.push({ ...emptyRow, ...item, id: item.boq_item_id || item.id });
            });
            const loadedSections = Object.keys(groups).map(key => ({
              project_division_id: groups[key].divId,
              dmr_subsection_id: groups[key].subId,
              dmr_category_id: groups[key].catId,
              items: groups[key].items,
            }));
            setSections(loadedSections);
          }
        }
      } catch (e) {
        console.error("Failed to fetch data", e);
      }
    };
    fetchData();
  }, [projectId]);
  // form rate fields (percent as typed) ⇄ the rates a line is priced with (fractions)
  const pct = (v) => +(v * 100).toFixed(4);
  function ratesToHeader(r) {
    return {
      labour_rate: r.unit_labour_rate, landed_uplift_pct: pct(r.landed_cost_uplift_rate),
      install_access_rate_pct: pct(r.installation_accessories_consumables_rate), transport_rate_pct: pct(r.transport_to_site_rate),
      equip_rate: pct(r.equipment_and_tools_rate), prelims_rate_pct: pct(r.prelim_site_mgmt_supevission_rate),
      margin_mat_rate_pct: pct(r.margin_markup_material_rate), margin_lab_rate_pct: pct(r.margin_markup_labour_rate),
    };
  }
  const headerToRates = (h) => ({
    unit_labour_rate: parseFloat(h.labour_rate) || 0,
    landed_cost_uplift_rate: (parseFloat(h.landed_uplift_pct) || 0) / 100,
    installation_accessories_consumables_rate: (parseFloat(h.install_access_rate_pct) || 0) / 100,
    transport_to_site_rate: (parseFloat(h.transport_rate_pct) || 0) / 100,
    equipment_and_tools_rate: (parseFloat(h.equip_rate) || 0) / 100,
    prelim_site_mgmt_supevission_rate: (parseFloat(h.prelims_rate_pct) || 0) / 100,
    margin_markup_material_rate: (parseFloat(h.margin_mat_rate_pct) || 0) / 100,
    margin_markup_labour_rate: (parseFloat(h.margin_lab_rate_pct) || 0) / 100,
  });
  // Calculate row derived values whenever inputs change
  const calculateRow = (row, currentHeader) => {
    // --- NEW RATE CALCULATIONS ---
    const priceSourceAmt = parseFloat(row.price_source_amount) || 0;
    
    row.ex_works_pds_rate = parseFloat(currentHeader.ex_works_pds_rate) || 0;
    row.ex_works_pds_amount = priceSourceAmt * row.ex_works_pds_rate;
    
    row.ex_works_euro_rate = parseFloat(currentHeader.ex_works_euro_rate) || 0;
    row.ex_works_euro_amount = priceSourceAmt * row.ex_works_euro_rate;
    
    row.ex_works_usd_rate = parseFloat(currentHeader.ex_works_usd_rate) || 0;
    row.ex_works_usd_amount = priceSourceAmt * row.ex_works_usd_rate;
    
    row.fob_usd_rate = (parseFloat(currentHeader.fob_usd_rate) || 0) / 100;
    row.fob_usd_amount = priceSourceAmt * row.fob_usd_rate;
    
    row.sea_freight_rate = (parseFloat(currentHeader.sea_freight_rate) || 0) / 100;
    row.sea_freight_amount = priceSourceAmt * row.sea_freight_rate;
    
    row.insurance_comsn_others_rate = (parseFloat(currentHeader.insurance_comsn_others_rate) || 0) / 100;
    row.insurance_comsn_others_amount = priceSourceAmt * row.insurance_comsn_others_rate;
    
    row.cf_usd_amount = row.fob_usd_amount + row.sea_freight_amount + row.insurance_comsn_others_amount;
    
    row.cf_naira_rate = parseFloat(currentHeader.cf_naira_rate) || 0;
    row.cf_naira_amount = row.cf_usd_amount * row.cf_naira_rate;
    
    row.duty_surcharges_rate = (parseFloat(currentHeader.duty_surcharges_rate) || 0) / 100;
    row.duty_surcharges_amount = row.cf_naira_amount * row.duty_surcharges_rate;
    
    row.clearing_charges_rate = (parseFloat(currentHeader.clearing_charges_rate) || 0) / 100;
    row.clearing_charges_amount = row.cf_naira_amount * row.clearing_charges_rate;
    
    row.agency_other_charges_rate = (parseFloat(currentHeader.agency_other_charges_rate) || 0) / 100;
    row.agency_other_charges_amount = row.cf_naira_amount * row.agency_other_charges_rate;

    // --- PRICING (the Excel BOQ template's method, with this form's rates) ---
    const rates = headerToRates(currentHeader);
    Object.assign(row, rates, costLine({
      budgeted_quantity: row.budgeted_quantity, basic_rate_material: row.basic_rate_material,
      factor: row.factor, unit_hour: row.unit_hour,
    }, rates));

    return row;
  };



  const recalculateParentFromComponents = (parentItem) => {
    if (!parentItem.has_components || !parentItem.components || parentItem.components.length === 0) return parentItem;
    
    const sumFields = [
      'amount', 'ex_works_pds_amount', 'ex_works_euro_amount', 'ex_works_usd_amount', 
      'fob_usd_amount', 'sea_freight_amount', 'insurance_comsn_others_amount', 
      'cf_usd_amount', 'cf_naira_amount', 'duty_surcharges_amount', 'clearing_charges_amount', 
      'agency_other_charges_amount', 'installation_accessories_consumables', 'transport_to_site',
      'total_material_delivered_site', 'total_labour', 'equipment_and_tools', 'prelim_site_mgmt_supevission',
      'total_amount_mat_lab', 'margin_markup_material_amount', 'margin_markup_labour_amount',
      'basic_rate_material', 'install_rate_labour', 'unit_rate',
      'budgeted_quantity', 'landed_cost_materials', 'unit_hour', 'price_source_amount',
      'unit_rate_material', 'unit_labour', 'total_unit_rate_mat_lab'
    ];
    
    // Reset totals
    sumFields.forEach(field => { parentItem[field] = 0; });
    
    parentItem.components.forEach(comp => {
      sumFields.forEach(field => {
        parentItem[field] += (parseFloat(comp[field]) || 0);
      });
    });
    
    // Recalculate parent factor based on sums
    if (parentItem.total_amount_mat_lab > 0) {
      parentItem.factor = parentItem.amount / parentItem.total_amount_mat_lab;
    } else {
      parentItem.factor = 0;
    }
    
    // Clear out parent rate fields since they are disabled and driven by child totals now
    // Actually, it's safer to just let the backend handle rate logic or zero them
    return parentItem;
  };

  const handleItemChange = (sIdx, iIdx, field, val) => {
    const newSections = [...sections];
    let updatedItem = { ...newSections[sIdx].items[iIdx], [field]: val };
    
    if (['price_source_amount', 'unit_hour', 'basic_rate_material', 'factor', 'budgeted_quantity'].includes(field)) {
      if (!updatedItem.has_components) {
        updatedItem = calculateRow(updatedItem, header);
      }
    }
    
    newSections[sIdx].items[iIdx] = updatedItem;
    setSections(newSections);
  };

  const handleToggleHasComponents = (sIdx, iIdx, checked) => {
    const newSections = [...sections];
    let item = newSections[sIdx].items[iIdx];
    item.has_components = checked;
    if (checked) {
      // Clear out parent numeric inputs and initialize with one component
      if (!item.components || item.components.length === 0) {
        item.components = [{ ...emptyComponent }];
      }
      item = recalculateParentFromComponents(item);
    } else {
      // Unchecked: restore to manual calculation based on its own inputs
      item.components = [];
      item = calculateRow(item, header);
    }
    newSections[sIdx].items[iIdx] = item;
    setSections(newSections);
  };

  const handleComponentChange = (sIdx, iIdx, cIdx, field, val) => {
    const newSections = [...sections];
    let parentItem = { ...newSections[sIdx].items[iIdx] };
    let comp = { ...parentItem.components[cIdx], [field]: val };
    
    if (['price_source_amount', 'unit_hour', 'basic_rate_material', 'factor', 'budgeted_quantity'].includes(field)) {
      comp = calculateRow(comp, header);
    }
    
    parentItem.components[cIdx] = comp;
    parentItem = recalculateParentFromComponents(parentItem);
    
    newSections[sIdx].items[iIdx] = parentItem;
    setSections(newSections);
  };

  const handleAddComponent = (sIdx, iIdx) => {
    const newSections = [...sections];
    const parentItem = { ...newSections[sIdx].items[iIdx] };
    parentItem.components = [...parentItem.components, { ...emptyComponent }];
    newSections[sIdx].items[iIdx] = parentItem;
    setSections(newSections);
    
    setTimeout(() => {
      if (tableContainerRef.current) {
        tableContainerRef.current.scrollTo({
          top: tableContainerRef.current.scrollHeight,
          behavior: 'smooth'
        });
      }
    }, 50);
  };

  const handleRemoveComponent = (sIdx, iIdx, cIdx) => {
    const newSections = [...sections];
    let parentItem = { ...newSections[sIdx].items[iIdx] };
    const newComps = [...parentItem.components];
    newComps.splice(cIdx, 1);
    parentItem.components = newComps;
    parentItem = recalculateParentFromComponents(parentItem);
    newSections[sIdx].items[iIdx] = parentItem;
    setSections(newSections);
  };

  const handleSectionChange = (sIdx, field, val) => {
    const newSections = [...sections];
    newSections[sIdx] = { ...newSections[sIdx], [field]: val };
    setSections(newSections);
  };

  const isFormValid = sections.length > 0 && sections.every(s => s.dmr_category_id && s.dmr_subsection_id);

  const handleGlobalRateChange = (field, val) => {
    const num = parseFloat(val) || 0;
    const newHeader = { ...header, [field]: num };
    setHeader(newHeader);
    const newSections = sections.map(section => ({
      ...section,
      items: section.items.map(item => calculateRow({...item}, newHeader))
    }));
    setSections(newSections);
  };

  const handleGlobalInstallRateChange = (pct) => handleGlobalRateChange('install_access_rate_pct', pct);
  const handleGlobalTransportRateChange = (pct) => handleGlobalRateChange('transport_rate_pct', pct);
  const handleGlobalPrelimsRateChange = (pct) => handleGlobalRateChange('prelims_rate_pct', pct);
  const handleGlobalUpliftRateChange = (pct) => handleGlobalRateChange('landed_uplift_pct', pct);
  const handleGlobalEquipRateChange = (rate) => handleGlobalRateChange('equip_rate', rate);
  const handleGlobalLabourRateChange = (rate) => handleGlobalRateChange('labour_rate', rate);
  const handleGlobalMarginMatRateChange = (pct) => handleGlobalRateChange('margin_mat_rate_pct', pct);
  const handleGlobalMarginLabRateChange = (pct) => handleGlobalRateChange('margin_lab_rate_pct', pct);
  const handleGlobalExWorksPdsRateChange = (val) => handleGlobalRateChange('ex_works_pds_rate', val);
  const handleGlobalExWorksEuroRateChange = (val) => handleGlobalRateChange('ex_works_euro_rate', val);
  const handleGlobalExWorksUsdRateChange = (val) => handleGlobalRateChange('ex_works_usd_rate', val);
  const handleGlobalFobUsdRateChange = (val) => handleGlobalRateChange('fob_usd_rate', val);
  const handleGlobalSeaFreightRateChange = (val) => handleGlobalRateChange('sea_freight_rate', val);
  const handleGlobalInsuranceRateChange = (val) => handleGlobalRateChange('insurance_comsn_others_rate', val);
  const handleGlobalCfNairaRateChange = (val) => handleGlobalRateChange('cf_naira_rate', val);
  const handleGlobalDutyRateChange = (val) => handleGlobalRateChange('duty_surcharges_rate', val);
  const handleGlobalClearingRateChange = (val) => handleGlobalRateChange('clearing_charges_rate', val);
  const handleGlobalAgencyRateChange = (val) => handleGlobalRateChange('agency_other_charges_rate', val);

  const handleAddRow = (sIdx) => {
    const newSections = [...sections];
    newSections[sIdx] = {
      ...newSections[sIdx],
      items: [...newSections[sIdx].items, { ...emptyRow }]
    };
    setSections(newSections);
    
    setTimeout(() => {
      if (tableContainerRef.current) {
        tableContainerRef.current.scrollTo({
          top: tableContainerRef.current.scrollHeight,
          behavior: 'smooth'
        });
      }
    }, 50);
  };

  const handleRemoveRow = (sIdx, iIdx) => {
    const newSections = [...sections];
    const newItems = [...newSections[sIdx].items];
    newItems.splice(iIdx, 1);
    newSections[sIdx] = { ...newSections[sIdx], items: newItems };
    setSections(newSections);
  };

  const handleAddSection = () => {
    setSections([...sections, { project_division_id: header.building_ids.length === 1 ? header.building_ids[0] : '', dmr_subsection_id: '', dmr_category_id: '', items: [{ ...emptyRow }] }]);
  };
  
  const handleRemoveSection = (sIdx) => {
    const newSections = [...sections];
    newSections.splice(sIdx, 1);
    setSections(newSections);
  };

  const allItems = sections.flatMap(s => s.items);
  const totalDue = allItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
  const vatRate = parseFloat(header.vat_rate) || 0;
  const vatAmount = totalDue * (vatRate / 100);
  const grandTotal = totalDue + vatAmount;

  const handleSubmit = async () => {
    if (!header.name) {
      toast?.show?.('BOQ Name is required', 'error');
      return;
    }
    if (!header.boq_revision_id) {
      toast?.show?.('Open this form from a revision in the BOQ Hub', 'error');
      return;
    }
    if (sections.length === 0 || allItems.length === 0) {
      toast?.show?.('Please add at least one line item', 'error');
      return;
    }
    
    const flattenedItems = [];
    for (let s = 0; s < sections.length; s++) {
      const section = sections[s];
      if (!section.project_division_id) {
        toast?.show?.(`Please choose the Building/Zone for section ${s + 1}`, 'error');
        return;
      }
      if (!section.dmr_subsection_id) {
        toast?.show?.(`Please select a Subsection for section ${s + 1}`, 'error');
        return;
      }
      if (!section.dmr_category_id) {
        toast?.show?.(`Please select a Category for section ${s + 1}`, 'error');
        return;
      }
      for (let i = 0; i < section.items.length; i++) {
        const item = section.items[i];
        if (!item.boq_code) {
          toast?.show?.(`Section ${s + 1}, Row ${i + 1} is missing Item No.`, 'error');
          return;
        }
        if (!item.description) {
          toast?.show?.(`Section ${s + 1}, Row ${i + 1} is missing Description`, 'error');
          return;
        }
        let mappedComponents = [];
        if (item.has_components && item.components && item.components.length > 0) {
          mappedComponents = item.components.map((comp) => ({
            ...comp,
            dmr_subsection_id: section.dmr_subsection_id,
            dmr_category_id: section.dmr_category_id || null,
            material_id: (comp.material_id && comp.material_id.trim() !== '') ? comp.material_id.trim() : null,
            project_division_id: section.project_division_id,
            budgeted_quantity: parseFloat(comp.budgeted_quantity) || 0,
            consultant_quantity: comp.consultant_quantity === '' || comp.consultant_quantity == null ? null : parseFloat(comp.consultant_quantity) || 0,
            basic_rate_material: parseFloat(comp.basic_rate_material) || 0,
            factor: parseFloat(comp.factor) || 0,
            landed_cost_materials: parseFloat(comp.landed_cost_materials) || 0,
            unit_hour: parseFloat(comp.unit_hour) || 0,
            price_source_amount: parseFloat(comp.price_source_amount) || 0,
            sub_component_code: comp.sub_component_code || comp.boq_code || '',
            description: comp.description || ''
          }));
        }

        flattenedItems.push({
          ...item,
          dmr_subsection_id: section.dmr_subsection_id,
          dmr_category_id: section.dmr_category_id || null,
          material_id: (item.material_id && item.material_id.trim() !== '') ? item.material_id.trim() : null,
          project_division_id: section.project_division_id,
          budgeted_quantity: parseFloat(item.budgeted_quantity) || 0,
          consultant_quantity: item.consultant_quantity === '' || item.consultant_quantity == null ? null : parseFloat(item.consultant_quantity) || 0,
          basic_rate_material: parseFloat(item.basic_rate_material) || 0,
          factor: parseFloat(item.factor) || 0,
          landed_cost_materials: parseFloat(item.landed_cost_materials) || 0,
          unit_hour: parseFloat(item.unit_hour) || 0,
          price_source_amount: parseFloat(item.price_source_amount) || 0,
          has_components: !!item.has_components,
          components: mappedComponents
        });
      }
    }

    setLoading(true);
    try {
      const payload = {
        project_id: projectId,
        boq_revision_id: header.boq_revision_id,
        building_ids: header.building_ids,
        status: header.status,
        name: header.name,
        description: header.description,
        entry_date: header.entry_date,
        attention: header.attention,
        total_amount: totalDue,
        vat_rate: vatRate / 100,
        grand_total: grandTotal,
        boq_items: flattenedItems,
      };

      if (existingBoqData && existingBoqData.id) {
        await updateBOQ(existingBoqData.id, payload);
      } else {
        await createBOQ(payload);
      }
      
      setSubmitResult({ type: 'success', message: `BOQ Form ${existingBoqData ? 'updated' : 'created'} successfully`, details: [] });
    } catch (err) {
      const details = err.data?.errors || [];
      setSubmitResult({
        type: 'error',
        message: err.message || 'An error occurred during submission.',
        details: details
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '0 24px 24px 24px', boxSizing: 'border-box', minHeight: '100%' }}>
      {onBack && !hideBackButton && (
        <div style={{ display: 'flex', marginBottom: 20 }}>
          <button
            onClick={onBack}
            style={{
              background: '#f1f5f9',
              border: '2px solid black',
              borderRadius: 30,
              padding: 4,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              outline: 'none'
            }}
            onMouseOver={e => e.currentTarget.style.transform = 'scale(1.03)'}
            onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}
          >
            <div style={{
              background: '#dbeafe',
              border: '2px solid #93c5fd',
              borderRadius: 24,
              padding: '4px 14px',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              color: '#0b318bff',
              fontWeight: 800,
              fontSize: 14,
              letterSpacing: '0.02em'
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
              Back to BOQ Listings
            </div>
          </button>
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24, border: '1px solid black', padding: 24, borderRadius: 12, background: 'var(--bg-surface)' }}>

      {/* Form Details */}
      <Card style={{ padding: 20, border: '1px solid black', borderRadius: 8 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <label style={{ fontSize: 13, fontWeight: 500, color: '#475569', marginBottom: 6, display: 'block' }}>BOQ Name</label>
            <Input value={header.name} onChange={val => setHeader({ ...header, name: val })} placeholder="e.g. Earthing and Lightning Protection" />
          </div>
          <div style={{ position: 'relative', zIndex: 99, width: '100%' }}>
            <style>{`.react-datepicker-wrapper { width: 100%; display: block; }`}</style>
            <DatePicker label="Entry Date" value={header.entry_date} onChange={date => setHeader({ ...header, entry_date: date })} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ fontSize: 13, fontWeight: 500, color: '#475569', marginBottom: 6, display: 'block' }}>Buildings / zones (the BOQ's tabs)</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {projectDivisions.map(d => {
                const id = d.project_division_id || d.id;
                const on = header.building_ids.includes(id);
                return (
                  <button key={id} type="button"
                    onClick={() => setHeader({ ...header, building_ids: on ? header.building_ids.filter(x => x !== id) : [...header.building_ids, id] })}
                    style={{ padding: '6px 12px', borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                      border: on ? '1.5px solid #0b318b' : '1px solid #cbd5e1', background: on ? '#0b318b' : '#ffffff', color: on ? '#ffffff' : '#334155' }}>
                    {on ? '✓ ' : ''}{d.name}{d.code ? ` (${d.code})` : ''}
                  </button>
                );
              })}
              {projectDivisions.length === 0 && <span style={{ fontSize: 13, color: '#94a3b8' }}>This project has no buildings/zones yet — add them in Project Setup.</span>}
            </div>
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 500, color: '#475569', marginBottom: 6, display: 'block' }}>Status</label>
            <Select 
              value={header.status} 
              onChange={val => setHeader({ ...header, status: val })}
              options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]}
            />
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 500, color: '#475569', marginBottom: 6, display: 'block' }}>Client Name</label>
            <Input value={header.attention} onChange={val => setHeader({ ...header, attention: val })} placeholder="Client contact name" />
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 500, color: '#475569', marginBottom: 6, display: 'block' }}>Description (Optional)</label>
            <Input value={header.description} onChange={val => setHeader({ ...header, description: val })} placeholder="Additional details..." />
          </div>
        </div>
      </Card>

      {/* Line Items Section */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minHeight: 0 }}>
        <div style={{ padding: '0 4px' }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1.2 }}>LINE ITEMS</span>
        </div>
        <Card style={{ display: 'flex', flexDirection: 'column', border: '1px solid #000', overflow: 'hidden', borderRadius: 8 }}>
          <div ref={tableContainerRef} style={{ paddingBottom: 40, overflow: 'auto', maxHeight: '600px' }}>
          <style>{`
            .boq-table input, .boq-table select {
              color: #000000 !important;
              font-weight: 500 !important;
            }
          `}</style>
          <table className="boq-table" style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, minWidth: 3500 }}>
            <thead style={{ background: '#0b318bff', position: 'sticky', top: 0, zIndex: 10 }}>
              <tr>
                <th style={{...thStyle, minWidth: 60, textAlign: 'center'}}>S/N</th>
                <th style={{...thStyle, minWidth: 150}}>Item No.</th>
                <th style={{...thStyle, minWidth: 350}}>Description</th>
                <th style={{...thStyle, minWidth: 300}}>Material</th>
                <th style={{...thStyle, minWidth: 150}}>Qty</th>
                <th style={{...thStyle, minWidth: 150}}><span style={{ textAlign: 'center', lineHeight: 1.2 }}>Consultant<br />Qty</span></th>
                <th style={{...thStyle, minWidth: 120}}>Unit</th>
                <th style={{...thStyle, minWidth: 150}}>Basic Rate (₦)</th>
                <th style={{...thStyle, minWidth: 150}}><span style={{ textAlign: 'center', lineHeight: 1.2 }}>Unit<br />Hour</span></th>
                <th style={{...thStyle, minWidth: 110}}>Factor</th>
                <th style={{...thStyle, minWidth: 120}}>Price Source<br/>Amount</th>
                <th style={thStyle}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <span style={{ textAlign: 'center', lineHeight: 1.2 }}>
                      Ex-Works<br/>PDS Amt
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', background: '#ffffff', borderRadius: 6, padding: '4px 8px', boxShadow: '0 2px 4px rgba(0,0,0,0.15)' }}>
                      <span style={{ color: '#0b318bff', fontSize: 13, fontWeight: 800, marginRight: 2 }}>£</span>
                      <input 
                        type="number" 
                        value={header.ex_works_pds_rate}
                        onChange={(e) => handleGlobalExWorksPdsRateChange(e.target.value)}
                        style={{ width: 55, background: 'transparent', border: 'none', color: '#0b318bff', textAlign: 'center', outline: 'none', fontSize: 13, fontWeight: 800 }}
                      />
                      
                    </div>
                  </div>
                </th>
                <th style={thStyle}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <span style={{ textAlign: 'center', lineHeight: 1.2 }}>
                      Ex-Works<br/>Euro Amt
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', background: '#ffffff', borderRadius: 6, padding: '4px 8px', boxShadow: '0 2px 4px rgba(0,0,0,0.15)' }}>
                      <span style={{ color: '#0b318bff', fontSize: 13, fontWeight: 800, marginRight: 2 }}>€</span>
                      <input 
                        type="number" 
                        value={header.ex_works_euro_rate}
                        onChange={(e) => handleGlobalExWorksEuroRateChange(e.target.value)}
                        style={{ width: 55, background: 'transparent', border: 'none', color: '#0b318bff', textAlign: 'center', outline: 'none', fontSize: 13, fontWeight: 800 }}
                      />
                      
                    </div>
                  </div>
                </th>
                <th style={thStyle}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <span style={{ textAlign: 'center', lineHeight: 1.2 }}>
                      Ex-Works<br/>USD Amt
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', background: '#ffffff', borderRadius: 6, padding: '4px 8px', boxShadow: '0 2px 4px rgba(0,0,0,0.15)' }}>
                      <span style={{ color: '#0b318bff', fontSize: 13, fontWeight: 800, marginRight: 2 }}>$</span>
                      <input 
                        type="number" 
                        value={header.ex_works_usd_rate}
                        onChange={(e) => handleGlobalExWorksUsdRateChange(e.target.value)}
                        style={{ width: 55, background: 'transparent', border: 'none', color: '#0b318bff', textAlign: 'center', outline: 'none', fontSize: 13, fontWeight: 800 }}
                      />
                      
                    </div>
                  </div>
                </th>
                <th style={thStyle}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <span style={{ textAlign: 'center', lineHeight: 1.2 }}>
                      FOB<br/>USD Amt
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', background: '#ffffff', borderRadius: 6, padding: '4px 8px', boxShadow: '0 2px 4px rgba(0,0,0,0.15)' }}>
                      
                      <input 
                        type="number" 
                        value={header.fob_usd_rate}
                        onChange={(e) => handleGlobalFobUsdRateChange(e.target.value)}
                        style={{ width: 45, background: 'transparent', border: 'none', color: '#0b318bff', textAlign: 'center', outline: 'none', fontSize: 13, fontWeight: 800 }}
                      />
                      <span style={{ color: '#0b318bff', fontSize: 13, fontWeight: 800, marginLeft: 2 }}>%</span>
                    </div>
                  </div>
                </th>
                <th style={thStyle}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <span style={{ textAlign: 'center', lineHeight: 1.2 }}>
                      Sea Freight<br/>Amt
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', background: '#ffffff', borderRadius: 6, padding: '4px 8px', boxShadow: '0 2px 4px rgba(0,0,0,0.15)' }}>
                      
                      <input 
                        type="number" 
                        value={header.sea_freight_rate}
                        onChange={(e) => handleGlobalSeaFreightRateChange(e.target.value)}
                        style={{ width: 45, background: 'transparent', border: 'none', color: '#0b318bff', textAlign: 'center', outline: 'none', fontSize: 13, fontWeight: 800 }}
                      />
                      <span style={{ color: '#0b318bff', fontSize: 13, fontWeight: 800, marginLeft: 2 }}>%</span>
                    </div>
                  </div>
                </th>
                <th style={thStyle}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <span style={{ textAlign: 'center', lineHeight: 1.2 }}>
                      Ins/Comsn<br/>Amt
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', background: '#ffffff', borderRadius: 6, padding: '4px 8px', boxShadow: '0 2px 4px rgba(0,0,0,0.15)' }}>
                      
                      <input 
                        type="number" 
                        value={header.insurance_comsn_others_rate}
                        onChange={(e) => handleGlobalInsuranceRateChange(e.target.value)}
                        style={{ width: 45, background: 'transparent', border: 'none', color: '#0b318bff', textAlign: 'center', outline: 'none', fontSize: 13, fontWeight: 800 }}
                      />
                      <span style={{ color: '#0b318bff', fontSize: 13, fontWeight: 800, marginLeft: 2 }}>%</span>
                    </div>
                  </div>
                </th>
                <th style={{...thStyle, minWidth: 120}}>C&F<br/>USD Amt</th>
                <th style={thStyle}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <span style={{ textAlign: 'center', lineHeight: 1.2 }}>
                      C&F<br/>Naira Amt
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', background: '#ffffff', borderRadius: 6, padding: '4px 8px', boxShadow: '0 2px 4px rgba(0,0,0,0.15)' }}>
                      <span style={{ color: '#0b318bff', fontSize: 13, fontWeight: 800, marginRight: 2 }}>₦</span>
                      <input 
                        type="number" 
                        value={header.cf_naira_rate}
                        onChange={(e) => handleGlobalCfNairaRateChange(e.target.value)}
                        style={{ width: 55, background: 'transparent', border: 'none', color: '#0b318bff', textAlign: 'center', outline: 'none', fontSize: 13, fontWeight: 800 }}
                      />
                      
                    </div>
                  </div>
                </th>
                <th style={thStyle}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <span style={{ textAlign: 'center', lineHeight: 1.2 }}>
                      Duty/Surch<br/>Amt
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', background: '#ffffff', borderRadius: 6, padding: '4px 8px', boxShadow: '0 2px 4px rgba(0,0,0,0.15)' }}>
                      
                      <input 
                        type="number" 
                        value={header.duty_surcharges_rate}
                        onChange={(e) => handleGlobalDutyRateChange(e.target.value)}
                        style={{ width: 45, background: 'transparent', border: 'none', color: '#0b318bff', textAlign: 'center', outline: 'none', fontSize: 13, fontWeight: 800 }}
                      />
                      <span style={{ color: '#0b318bff', fontSize: 13, fontWeight: 800, marginLeft: 2 }}>%</span>
                    </div>
                  </div>
                </th>
                <th style={thStyle}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <span style={{ textAlign: 'center', lineHeight: 1.2 }}>
                      Clearing<br/>Amt
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', background: '#ffffff', borderRadius: 6, padding: '4px 8px', boxShadow: '0 2px 4px rgba(0,0,0,0.15)' }}>
                      
                      <input 
                        type="number" 
                        value={header.clearing_charges_rate}
                        onChange={(e) => handleGlobalClearingRateChange(e.target.value)}
                        style={{ width: 45, background: 'transparent', border: 'none', color: '#0b318bff', textAlign: 'center', outline: 'none', fontSize: 13, fontWeight: 800 }}
                      />
                      <span style={{ color: '#0b318bff', fontSize: 13, fontWeight: 800, marginLeft: 2 }}>%</span>
                    </div>
                  </div>
                </th>
                <th style={thStyle}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <span style={{ textAlign: 'center', lineHeight: 1.2 }}>
                      Agency<br/>Amt
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', background: '#ffffff', borderRadius: 6, padding: '4px 8px', boxShadow: '0 2px 4px rgba(0,0,0,0.15)' }}>
                      
                      <input 
                        type="number" 
                        value={header.agency_other_charges_rate}
                        onChange={(e) => handleGlobalAgencyRateChange(e.target.value)}
                        style={{ width: 45, background: 'transparent', border: 'none', color: '#0b318bff', textAlign: 'center', outline: 'none', fontSize: 13, fontWeight: 800 }}
                      />
                      <span style={{ color: '#0b318bff', fontSize: 13, fontWeight: 800, marginLeft: 2 }}>%</span>
                    </div>
                  </div>
                </th>
                <th style={thStyle}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <span style={{ textAlign: 'center', lineHeight: 1.2 }}>
                      Installation<br />Accessories &<br />Consumables
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', background: '#ffffff', borderRadius: 6, padding: '4px 8px', boxShadow: '0 2px 4px rgba(0,0,0,0.15)' }}>
                      <input 
                        type="number" 
                        value={header.install_access_rate_pct}
                        onChange={(e) => handleGlobalInstallRateChange(e.target.value)}
                        style={{ width: 45, background: 'transparent', border: 'none', color: '#0b318bff', textAlign: 'center', outline: 'none', fontSize: 13, fontWeight: 800 }}
                      />
                      <span style={{ color: '#0b318bff', fontSize: 13, fontWeight: 800, marginLeft: 2 }}>%</span>
                    </div>
                  </div>
                </th>
                <th style={thStyle}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <span style={{ textAlign: 'center', lineHeight: 1.2 }}>
                      Transport<br />To<br />Site
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', background: '#ffffff', borderRadius: 6, padding: '4px 8px', boxShadow: '0 2px 4px rgba(0,0,0,0.15)' }}>
                      <input 
                        type="number" 
                        value={header.transport_rate_pct}
                        onChange={(e) => handleGlobalTransportRateChange(e.target.value)}
                        style={{ width: 45, background: 'transparent', border: 'none', color: '#0b318bff', textAlign: 'center', outline: 'none', fontSize: 13, fontWeight: 800 }}
                      />
                      <span style={{ color: '#0b318bff', fontSize: 13, fontWeight: 800, marginLeft: 2 }}>%</span>
                    </div>
                  </div>
                </th>
                <th style={thStyle}>Unit Rate Mat.</th>
                <th style={thStyle}>Total Mat. Delivered</th>
                <th style={thStyle}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <span style={{ textAlign: 'center', lineHeight: 1.2 }}>
                      Unit<br />Labour
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', background: '#ffffff', borderRadius: 6, padding: '4px 8px', boxShadow: '0 2px 4px rgba(0,0,0,0.15)' }}>
                      <span style={{ color: '#0b318bff', fontSize: 13, fontWeight: 800, marginRight: 2 }}>₦</span>
                      <input 
                        type="number" 
                        value={header.labour_rate}
                        onChange={(e) => handleGlobalLabourRateChange(e.target.value)}
                        style={{ width: 55, background: 'transparent', border: 'none', color: '#0b318bff', textAlign: 'center', outline: 'none', fontSize: 13, fontWeight: 800 }}
                      />
                    </div>
                  </div>
                </th>
                <th style={thStyle}>Total Labour</th>
                <th style={thStyle}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <span style={{ textAlign: 'center', lineHeight: 1.2 }}>
                      Equip &<br />Tools
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', background: '#ffffff', borderRadius: 6, padding: '4px 8px', boxShadow: '0 2px 4px rgba(0,0,0,0.15)' }}>
                      <input 
                        type="number" 
                        value={header.equip_rate}
                        onChange={(e) => handleGlobalEquipRateChange(e.target.value)}
                        title="% of the labour rate"
                        style={{ width: 45, background: 'transparent', border: 'none', color: '#0b318bff', textAlign: 'center', outline: 'none', fontSize: 13, fontWeight: 800 }}
                      />
                      <span style={{ color: '#0b318bff', fontSize: 13, fontWeight: 800, marginLeft: 2 }}>%</span>
                    </div>
                  </div>
                </th>
                <th style={thStyle}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <span style={{ textAlign: 'center', lineHeight: 1.2 }}>
                      Prelim &<br />Site
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', background: '#ffffff', borderRadius: 6, padding: '4px 8px', boxShadow: '0 2px 4px rgba(0,0,0,0.15)' }}>
                      <input 
                        type="number" 
                        value={header.prelims_rate_pct}
                        onChange={(e) => handleGlobalPrelimsRateChange(e.target.value)}
                        style={{ width: 45, background: 'transparent', border: 'none', color: '#0b318bff', textAlign: 'center', outline: 'none', fontSize: 13, fontWeight: 800 }}
                      />
                      <span style={{ color: '#0b318bff', fontSize: 13, fontWeight: 800, marginLeft: 2 }}>%</span>
                    </div>
                  </div>
                </th>
                <th style={thStyle}>Total Unit Rate (M+L)</th>
                <th style={thStyle}>Total Amt (M+L)</th>
                <th style={thStyle}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <span style={{ textAlign: 'center', lineHeight: 1.2 }}>
                      Margin<br />Material
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', background: '#ffffff', borderRadius: 6, padding: '4px 8px', boxShadow: '0 2px 4px rgba(0,0,0,0.15)' }}>
                      <input 
                        type="number" 
                        value={header.margin_mat_rate_pct}
                        onChange={(e) => handleGlobalMarginMatRateChange(e.target.value)}
                        style={{ width: 45, background: 'transparent', border: 'none', color: '#0b318bff', textAlign: 'center', outline: 'none', fontSize: 13, fontWeight: 800 }}
                      />
                      <span style={{ color: '#0b318bff', fontSize: 13, fontWeight: 800, marginLeft: 2 }}>%</span>
                    </div>
                  </div>
                </th>
                <th style={thStyle}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <span style={{ textAlign: 'center', lineHeight: 1.2 }}>
                      Margin<br />Labour
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', background: '#ffffff', borderRadius: 6, padding: '4px 8px', boxShadow: '0 2px 4px rgba(0,0,0,0.15)' }}>
                      <input 
                        type="number" 
                        value={header.margin_lab_rate_pct}
                        onChange={(e) => handleGlobalMarginLabRateChange(e.target.value)}
                        style={{ width: 45, background: 'transparent', border: 'none', color: '#0b318bff', textAlign: 'center', outline: 'none', fontSize: 13, fontWeight: 800 }}
                      />
                      <span style={{ color: '#0b318bff', fontSize: 13, fontWeight: 800, marginLeft: 2 }}>%</span>
                    </div>
                  </div>
                </th>
                <th style={thStyle}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <span style={{ textAlign: 'center', lineHeight: 1.2 }}>Landed Cost<br />(Mat)</span>
                    <div style={{ display: 'flex', alignItems: 'center', background: '#ffffff', borderRadius: 6, padding: '4px 8px', boxShadow: '0 2px 4px rgba(0,0,0,0.15)' }}>
                      <input
                        type="number"
                        value={header.landed_uplift_pct}
                        onChange={(e) => handleGlobalUpliftRateChange(e.target.value)}
                        title="Landed cost uplift on the basic rate (freight, duty, clearing)"
                        style={{ width: 45, background: 'transparent', border: 'none', color: '#0b318bff', textAlign: 'center', outline: 'none', fontSize: 13, fontWeight: 800 }}
                      />
                      <span style={{ color: '#0b318bff', fontSize: 13, fontWeight: 800, marginLeft: 2 }}>%</span>
                    </div>
                  </div>
                </th>
                <th style={thStyle}>Install Rate (Lab)</th>
                <th style={thStyle}>Unit Rate</th>
                <th style={thStyle}>Amount</th>
                <th style={thStyle}></th>
              </tr>
            </thead>
            <tbody>
              {sections.map((section, sIdx) => (
                <React.Fragment key={sIdx}>
                  <tr style={{ background: '#f1f5f9', borderBottom: '2px solid #cbd5e1' }}>
                    <td colSpan={37} style={{ padding: '12px 16px', textAlign: 'left' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                        <span style={{ fontWeight: 700, color: '#334155' }}>Building/Zone:<span style={{ color: '#ef4444' }}> *</span></span>
                        <Select
                          value={section.project_division_id}
                          onChange={(val) => handleSectionChange(sIdx, 'project_division_id', val)}
                          options={[
                            { value: '', label: header.building_ids.length ? 'Select a building...' : 'Tick the BOQ’s buildings above first' },
                            ...projectDivisions.filter(d => header.building_ids.includes(d.project_division_id || d.id))
                              .map(d => ({ value: d.project_division_id || d.id, label: d.name }))
                          ]}
                          style={{ width: 240 }}
                        />
                        <span style={{ fontWeight: 700, color: '#334155', marginLeft: 16 }}>Category:<span style={{ color: '#ef4444' }}> *</span></span>
                        <Select 
                          value={section.dmr_category_id}
                          onChange={(val) => handleSectionChange(sIdx, 'dmr_category_id', val)}
                          options={[
                            {value: '', label: 'Select a category...'},
                            ...categories.map(cat => ({ value: cat.id, label: cat.name }))
                          ]}
                          style={{ width: 300 }}
                        />
                        <span style={{ fontWeight: 700, color: '#334155', marginLeft: 16 }}>Select Subsection:<span style={{ color: '#ef4444' }}> *</span></span>
                        <Select 
                          value={section.dmr_subsection_id}
                          onChange={(val) => handleSectionChange(sIdx, 'dmr_subsection_id', val)}
                          options={[
                            {value: '', label: 'Select a subsection...'},
                            ...subsections.map(sub => ({ value: sub.id, label: sub.name }))
                          ]}
                          style={{ width: 300 }}
                        />
                        {sections.length > 1 && (
                          <Button onClick={() => handleRemoveSection(sIdx)} style={{ background: '#ef4444', color: 'white', border: 'none', padding: '4px 12px', height: '36px', borderRadius: 4 }}>
                            Remove Section
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {section.items.map((item, iIdx) => (
                    <React.Fragment key={iIdx}>
                      <tr style={{ borderBottom: '1px solid #e2e8f0', background: item.has_components ? '#f8fafc' : 'transparent' }}>
                  <td style={{...tdStyle, textAlign: 'center', fontWeight: 600, color: '#475569'}}>{iIdx + 1}</td>
                  <td style={tdStyle}>
                    <Input value={item.boq_code} onChange={val => handleItemChange(sIdx, iIdx, 'boq_code', val)} placeholder="Code" disabled={!!item.id} />
                  </td>
                  <td style={tdStyle}>
                    <Input value={item.description} onChange={val => handleItemChange(sIdx, iIdx, 'description', val)} placeholder="Description" disabled={!!item.id} />
                  </td>
                  <td style={tdStyle}>
                    <SearchableSelect
                      style={{ width: '100%' }}
                      value={item.material_id}
                      onChange={v => handleItemChange(sIdx, iIdx, 'material_id', v)}
                      disabled={!!item.id}
                      placeholder="- None -"
                      options={materialsList.map(m => ({ value: m.material_id || m.id, label: `${m.code} - ${m.name}` }))}
                    />
                  </td>
                  <td style={tdStyle}>
                    <Input type="number" value={item.budgeted_quantity} onChange={val => handleItemChange(sIdx, iIdx, 'budgeted_quantity', val)} placeholder="0" disabled={!!item.id || item.has_components} />
                  </td>
                  <td style={tdStyle}>
                    <Input type="number" value={item.consultant_quantity ?? ''} onChange={val => handleItemChange(sIdx, iIdx, 'consultant_quantity', val)} placeholder="Same" disabled={!!item.id || item.has_components} />
                  </td>
                  <td style={tdStyle}>
                    <SearchableSelect
                      style={{ width: '100%' }}
                      value={item.unit_of_measure}
                      onChange={v => handleItemChange(sIdx, iIdx, 'unit_of_measure', v)}
                      disabled={!!item.id}
                      options={['PCS', 'MT', 'KG', 'LS', 'Lot', 'M'].map(s => ({ value: s, label: s }))}
                    />
                  </td>
                  <td style={tdStyle}>
                    <Input type="number" value={item.basic_rate_material} onChange={val => handleItemChange(sIdx, iIdx, 'basic_rate_material', val)} placeholder="0.00" disabled={!!item.id || item.has_components} />
                  </td>
                  <td style={tdStyle}>
                    <Input type="number" value={item.unit_hour} onChange={val => handleItemChange(sIdx, iIdx, 'unit_hour', val)} placeholder="0.00" disabled={!!item.id || item.has_components} />
                  </td>
                  <td style={tdStyle}>
                    <Input type="number" value={item.factor} onChange={val => handleItemChange(sIdx, iIdx, 'factor', val)} placeholder="1" disabled={!!item.id || item.has_components} />
                  </td>
                  <td style={tdStyle}><Input type="number" value={item.price_source_amount} onChange={val => handleItemChange(sIdx, iIdx, 'price_source_amount', val)} placeholder="0.00" disabled={!!item.id || item.has_components} /></td>
                  <td style={tdStyle}><div style={readOnlyBoxStyle}>{item.ex_works_pds_amount?.toLocaleString(undefined, {minimumFractionDigits: 2})}</div></td>
                  <td style={tdStyle}><div style={readOnlyBoxStyle}>{item.ex_works_euro_amount?.toLocaleString(undefined, {minimumFractionDigits: 2})}</div></td>
                  <td style={tdStyle}><div style={readOnlyBoxStyle}>{item.ex_works_usd_amount?.toLocaleString(undefined, {minimumFractionDigits: 2})}</div></td>
                  <td style={tdStyle}><div style={readOnlyBoxStyle}>{item.fob_usd_amount?.toLocaleString(undefined, {minimumFractionDigits: 2})}</div></td>
                  <td style={tdStyle}><div style={readOnlyBoxStyle}>{item.sea_freight_amount?.toLocaleString(undefined, {minimumFractionDigits: 2})}</div></td>
                  <td style={tdStyle}><div style={readOnlyBoxStyle}>{item.insurance_comsn_others_amount?.toLocaleString(undefined, {minimumFractionDigits: 2})}</div></td>
                  <td style={tdStyle}><div style={readOnlyBoxStyle}>{item.cf_usd_amount?.toLocaleString(undefined, {minimumFractionDigits: 2})}</div></td>
                  <td style={tdStyle}><div style={readOnlyBoxStyle}>{item.cf_naira_amount?.toLocaleString(undefined, {minimumFractionDigits: 2})}</div></td>
                  <td style={tdStyle}><div style={readOnlyBoxStyle}>{item.duty_surcharges_amount?.toLocaleString(undefined, {minimumFractionDigits: 2})}</div></td>
                  <td style={tdStyle}><div style={readOnlyBoxStyle}>{item.clearing_charges_amount?.toLocaleString(undefined, {minimumFractionDigits: 2})}</div></td>
                  <td style={tdStyle}><div style={readOnlyBoxStyle}>{item.agency_other_charges_amount?.toLocaleString(undefined, {minimumFractionDigits: 2})}</div></td>
                  <td style={tdStyle}><div style={readOnlyBoxStyle}>{item.installation_accessories_consumables?.toLocaleString()}</div></td>
                  <td style={tdStyle}><div style={readOnlyBoxStyle}>{item.transport_to_site?.toLocaleString()}</div></td>
                  <td style={tdStyle}><div style={readOnlyBoxStyle}>{item.unit_rate_material?.toLocaleString()}</div></td>
                  <td style={tdStyle}><div style={readOnlyBoxStyle}>{item.total_material_delivered_site?.toLocaleString()}</div></td>
                  <td style={tdStyle}><div style={readOnlyBoxStyle}>{item.unit_labour?.toLocaleString()}</div></td>
                  <td style={tdStyle}><div style={readOnlyBoxStyle}>{item.total_labour?.toLocaleString()}</div></td>
                  <td style={tdStyle}><div style={readOnlyBoxStyle}>{item.equipment_and_tools?.toLocaleString()}</div></td>
                  <td style={tdStyle}><div style={readOnlyBoxStyle}>{item.prelim_site_mgmt_supevission?.toLocaleString()}</div></td>
                  <td style={tdStyle}><div style={readOnlyBoxStyle}>{item.total_unit_rate_mat_lab?.toLocaleString()}</div></td>
                  <td style={tdStyle}><div style={readOnlyBoxStyle}>{item.total_amount_mat_lab?.toLocaleString()}</div></td>
                  <td style={tdStyle}><div style={readOnlyBoxStyle}>{item.margin_markup_material_amount?.toLocaleString()}</div></td>
                  <td style={tdStyle}><div style={readOnlyBoxStyle}>{item.margin_markup_labour_amount?.toLocaleString()}</div></td>
                  <td style={tdStyle}>
                    <div style={{ ...readOnlyBoxStyle, fontWeight: 500 }}>₦ {item.landed_cost_materials?.toLocaleString()}</div>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ ...readOnlyBoxStyle, fontWeight: 500 }}>₦ {item.install_rate_labour?.toLocaleString()}</div>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ ...readOnlyBoxStyle, fontWeight: 600, color: '#071e53', background: '#e2e8f0' }}>₦ {item.unit_rate?.toLocaleString()}</div>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ ...readOnlyBoxStyle, fontWeight: 600, color: '#071e53', background: '#e2e8f0' }}>₦ {item.amount?.toLocaleString()}</div>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: 11, fontWeight: 700, color: '#475569' }}>
                        <input 
                          type="checkbox" 
                          checked={item.has_components || false} 
                          onChange={(e) => handleToggleHasComponents(sIdx, iIdx, e.target.checked)} 
                          style={{ width: 14, height: 14, accentColor: '#ec4899', cursor: 'pointer' }}
                        />
                        Sub
                      </label>
                      {item.has_components && (
                        <Button title="Add Component" variant="ghost" onClick={() => handleAddComponent(sIdx, iIdx)} style={{ color: '#10b981', padding: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#d1fae5', borderRadius: '50%' }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14"></path><path d="M5 12h14"></path></svg>
                        </Button>
                      )}
                      {section.items.length > 1 && !item.id && (
                        <Button title="Remove Item" variant="ghost" onClick={() => handleRemoveRow(sIdx, iIdx)} style={{ color: '#ef4444', padding: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 6h18"></path>
                            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                          </svg>
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
                {item.has_components && item.components && item.components.map((comp, cIdx) => (
                  <tr key={`comp-${cIdx}`} style={{ borderBottom: '1px dotted #e2e8f0', backgroundColor: '#fdf2f8' }}>
                    <td style={{...tdStyle, textAlign: 'center', fontWeight: 600, color: '#ec4899'}}>{iIdx + 1}.{cIdx + 1}</td>
                    <td style={tdStyle}>
                      <Input value={comp.sub_component_code} onChange={val => handleComponentChange(sIdx, iIdx, cIdx, 'sub_component_code', val)} placeholder="Sub Code" disabled={!!item.id} />
                    </td>
                    <td style={tdStyle}>
                      <Input value={comp.description} onChange={val => handleComponentChange(sIdx, iIdx, cIdx, 'description', val)} placeholder="Description" disabled={!!item.id} />
                    </td>
                    <td style={tdStyle}>
                      <SearchableSelect
                        style={{ width: '100%' }}
                        value={comp.material_id}
                        onChange={v => handleComponentChange(sIdx, iIdx, cIdx, 'material_id', v)}
                        disabled={!!item.id}
                        placeholder="- None -"
                        options={materialsList.map(m => ({ value: m.material_id || m.id, label: `${m.code} - ${m.name}` }))}
                      />
                    </td>
                    <td style={tdStyle}>
                      <Input type="number" value={comp.budgeted_quantity} onChange={val => handleComponentChange(sIdx, iIdx, cIdx, 'budgeted_quantity', val)} placeholder="0" disabled={!!item.id} />
                    </td>
                    <td style={tdStyle}>
                      <Input type="number" value={comp.consultant_quantity ?? ''} onChange={val => handleComponentChange(sIdx, iIdx, cIdx, 'consultant_quantity', val)} placeholder="Same" disabled={!!item.id} />
                    </td>
                    <td style={tdStyle}>
                      <SearchableSelect
                        style={{ width: '100%' }}
                        value={comp.unit_of_measure}
                        onChange={v => handleComponentChange(sIdx, iIdx, cIdx, 'unit_of_measure', v)}
                        disabled={!!item.id}
                        options={['PCS', 'MT', 'KG', 'LS', 'Lot', 'M'].map(s => ({ value: s, label: s }))}
                      />
                    </td>
                    <td style={tdStyle}>
                      <Input type="number" value={comp.basic_rate_material} onChange={val => handleComponentChange(sIdx, iIdx, cIdx, 'basic_rate_material', val)} placeholder="0.00" disabled={!!item.id} />
                    </td>
                    <td style={tdStyle}>
                      <Input type="number" value={comp.unit_hour} onChange={val => handleComponentChange(sIdx, iIdx, cIdx, 'unit_hour', val)} placeholder="0.00" disabled={!!item.id} />
                    </td>
                    <td style={tdStyle}>
                      <Input type="number" value={comp.factor} onChange={val => handleComponentChange(sIdx, iIdx, cIdx, 'factor', val)} placeholder="1" disabled={!!item.id} />
                    </td>
                    <td style={tdStyle}><Input type="number" value={comp.price_source_amount} onChange={val => handleComponentChange(sIdx, iIdx, cIdx, 'price_source_amount', val)} placeholder="0.00" disabled={!!item.id} /></td>
                    <td style={tdStyle}><div style={readOnlyBoxStyle}>{comp.ex_works_pds_amount?.toLocaleString(undefined, {minimumFractionDigits: 2})}</div></td>
                    <td style={tdStyle}><div style={readOnlyBoxStyle}>{comp.ex_works_euro_amount?.toLocaleString(undefined, {minimumFractionDigits: 2})}</div></td>
                    <td style={tdStyle}><div style={readOnlyBoxStyle}>{comp.ex_works_usd_amount?.toLocaleString(undefined, {minimumFractionDigits: 2})}</div></td>
                    <td style={tdStyle}><div style={readOnlyBoxStyle}>{comp.fob_usd_amount?.toLocaleString(undefined, {minimumFractionDigits: 2})}</div></td>
                    <td style={tdStyle}><div style={readOnlyBoxStyle}>{comp.sea_freight_amount?.toLocaleString(undefined, {minimumFractionDigits: 2})}</div></td>
                    <td style={tdStyle}><div style={readOnlyBoxStyle}>{comp.insurance_comsn_others_amount?.toLocaleString(undefined, {minimumFractionDigits: 2})}</div></td>
                    <td style={tdStyle}><div style={readOnlyBoxStyle}>{comp.cf_usd_amount?.toLocaleString(undefined, {minimumFractionDigits: 2})}</div></td>
                    <td style={tdStyle}><div style={readOnlyBoxStyle}>{comp.cf_naira_amount?.toLocaleString(undefined, {minimumFractionDigits: 2})}</div></td>
                    <td style={tdStyle}><div style={readOnlyBoxStyle}>{comp.duty_surcharges_amount?.toLocaleString(undefined, {minimumFractionDigits: 2})}</div></td>
                    <td style={tdStyle}><div style={readOnlyBoxStyle}>{comp.clearing_charges_amount?.toLocaleString(undefined, {minimumFractionDigits: 2})}</div></td>
                    <td style={tdStyle}><div style={readOnlyBoxStyle}>{comp.agency_other_charges_amount?.toLocaleString(undefined, {minimumFractionDigits: 2})}</div></td>
                    <td style={tdStyle}><div style={readOnlyBoxStyle}>{comp.installation_accessories_consumables?.toLocaleString()}</div></td>
                    <td style={tdStyle}><div style={readOnlyBoxStyle}>{comp.transport_to_site?.toLocaleString()}</div></td>
                    <td style={tdStyle}><div style={readOnlyBoxStyle}>{comp.unit_rate_material?.toLocaleString()}</div></td>
                    <td style={tdStyle}><div style={readOnlyBoxStyle}>{comp.total_material_delivered_site?.toLocaleString()}</div></td>
                    <td style={tdStyle}><div style={readOnlyBoxStyle}>{comp.unit_labour?.toLocaleString()}</div></td>
                    <td style={tdStyle}><div style={readOnlyBoxStyle}>{comp.total_labour?.toLocaleString()}</div></td>
                    <td style={tdStyle}><div style={readOnlyBoxStyle}>{comp.equipment_and_tools?.toLocaleString()}</div></td>
                    <td style={tdStyle}><div style={readOnlyBoxStyle}>{comp.prelim_site_mgmt_supevission?.toLocaleString()}</div></td>
                    <td style={tdStyle}><div style={readOnlyBoxStyle}>{comp.total_unit_rate_mat_lab?.toLocaleString()}</div></td>
                    <td style={tdStyle}><div style={readOnlyBoxStyle}>{comp.total_amount_mat_lab?.toLocaleString()}</div></td>
                    <td style={tdStyle}><div style={readOnlyBoxStyle}>{comp.margin_markup_material_amount?.toLocaleString()}</div></td>
                    <td style={tdStyle}><div style={readOnlyBoxStyle}>{comp.margin_markup_labour_amount?.toLocaleString()}</div></td>
                    <td style={tdStyle}><div style={{ ...readOnlyBoxStyle, fontWeight: 500 }}>₦ {comp.landed_cost_materials?.toLocaleString()}</div></td>
                    <td style={tdStyle}><div style={{ ...readOnlyBoxStyle, fontWeight: 500 }}>₦ {comp.install_rate_labour?.toLocaleString()}</div></td>
                    <td style={tdStyle}><div style={{ ...readOnlyBoxStyle, fontWeight: 600, color: '#071e53', background: '#e2e8f0' }}>₦ {comp.unit_rate?.toLocaleString()}</div></td>
                    <td style={tdStyle}><div style={{ ...readOnlyBoxStyle, fontWeight: 600, color: '#071e53', background: '#e2e8f0' }}>₦ {comp.amount?.toLocaleString()}</div></td>
                    <td style={tdStyle}>
                      {!item.id && (
                        <Button title="Remove Sub-component" variant="ghost" onClick={() => handleRemoveComponent(sIdx, iIdx, cIdx)} style={{ color: '#ef4444', padding: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 6h18"></path>
                            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                          </svg>
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
                  <tr>
                    <td colSpan={37} style={{ padding: '8px 16px', textAlign: 'right', background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                      <Button onClick={() => handleAddRow(sIdx)} style={{ background: '#0ea5e9', color: 'white', padding: '6px 12px', fontSize: 13, border: 'none', borderRadius: 4 }}>
                        Add Line Item
                      </Button>
                    </td>
                  </tr>
                </React.Fragment>
              ))}
              {sections.length === 0 && (
                <tr>
                  <td colSpan={37} style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>
                    No subsections added yet. Click "Add Subsection" below.
                  </td>
                </tr>
               )}
            </tbody>
          </table>
        </div>
        
        <div style={{ padding: 16, borderTop: '2px solid #3c71b5ff', background: '#d4d7d8ff', display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={handleAddSection} style={{ background: '#0b318bff', color: 'white', border: 'none', padding: '8px 16px', borderRadius: 8 }}>
            Add Subsection
          </Button>
        </div>
        
        {/* Totals Block */}
        <div style={{ padding: 16, background: '#d4d7d8ff', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end' }}>
          <div style={{ width: 300 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
              <span style={{ fontSize: 14, color: '#475569' }}>Total Due:</span>
              <span style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>₦ {totalDue.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', alignItems: 'center' }}>
              <span style={{ fontSize: 14, color: '#475569', display: 'flex', alignItems: 'center', gap: 8 }}>
                Add VAT
                <Input 
                  type="number" 
                  value={header.vat_rate} 
                  onChange={val => setHeader({ ...header, vat_rate: val })} 
                  style={{ width: 60, padding: '4px 8px', height: 28, fontSize: 13 }} 
                /> %:
              </span>
              <span style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>₦ {vatAmount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: '1px solid #cbd5e1', marginTop: 8 }}>
              <span style={{ fontSize: 16, fontWeight: 700, color: '#071e53' }}>GRAND TOTAL:</span>
              <span style={{ fontSize: 16, fontWeight: 700, color: '#071e53' }}>₦ {grandTotal.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
            </div>
          </div>
        </div>
      </Card>
      </div>
      {/* Modal for Success/Error */}
      {submitResult && (
        <Modal 
          title={submitResult.type === 'success' ? 'Success' : 'Submission Failed'} 
          headerBg="#0b318bff"
          headerColor="#ffffff"
          onClose={() => {
            if (submitResult.type === 'success') {
              onBack();
            } else {
              setSubmitResult(null);
            }
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ color: submitResult.type === 'success' ? '#10b981' : '#ef4444', fontSize: 16, fontWeight: 600 }}>
              {submitResult.message}
            </div>
            {submitResult.details && submitResult.details.length > 0 && (
              <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', padding: 12, borderRadius: 8 }}>
                <ul style={{ margin: 0, paddingLeft: 20, color: '#991b1b', fontSize: 14 }}>
                  {submitResult.details.map((d, idx) => (
                    <li key={idx} style={{ marginBottom: 6 }}>
                      <strong style={{ textTransform: 'capitalize' }}>
                        {(d.field || '').split('->').map(s => s.trim().replace(/_/g, ' ')).join(' > ')}:
                      </strong> {d.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
              <Button onClick={() => {
                if (submitResult.type === 'success') {
                  onBack();
                } else {
                  setSubmitResult(null);
                }
              }} variant={submitResult.type === 'success' ? 'success' : 'danger'}>
                {submitResult.type === 'success' ? 'Continue' : 'Close'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* FOOTER */}
      <div className="mobile-form-footer flex-mobile-col" style={{ background: '#0b318bff', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '0 -24px -24px -24px', borderRadius: '0 0 12px 12px' }}>
        <Button onClick={onBack} style={{ background: '#ef4444', color: '#fff', border: 'none', padding: '14px 48px', fontWeight: 700, fontSize: 16, borderRadius: 8, minWidth: 160 }}>
          Cancel
        </Button>
        <Button disabled={loading || !isFormValid} loading={loading} onClick={handleSubmit} style={{ background: '#10b981', color: '#fff', border: 'none', padding: '14px 48px', fontWeight: 700, fontSize: 16, borderRadius: 8, minWidth: 160 }}>
          Save
        </Button>
      </div>
      
      </div>
    </div>
  );
}

const thStyle = {
  padding: '14px 16px',
  textAlign: 'center',
  fontSize: 12,
  fontWeight: 900,
  color: '#ffffff',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  border: '1px solid rgba(255, 255, 255, 0.4)',
  backgroundClip: 'padding-box'
};

const tdStyle = {
  padding: '8px 16px',
  verticalAlign: 'middle',
  textAlign: 'center',
  border: '1px solid #e2e8f0'
};

const readOnlyBoxStyle = {
  display: 'inline-block',
  width: '100%',
  minWidth: '80px',
  padding: '8px 12px',
  background: '#f1f5f9',
  border: '1px solid #e2e8f0',
  borderRadius: 6,
  fontSize: 13,
  fontWeight: 600,
  color: '#000000',
  cursor: 'not-allowed',
  boxSizing: 'border-box'
};
